import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  ContactReferenceModel,
  MerchantAccount,
  PaymentMethodModel,
  PurchasePaymentIntentModel,
  calculateRenewalDate,
  ResumeSubscriptionModel,
  SubscriptionDetailModel,
} from "../models";
import { 
  MerchantAccountService,
  PaymentMethodService,
  SubscriptionService,
} from "../services";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  Loader,
  ModalComponent,
  PriceDescriptionBlock,
  SapphireBlueButton,
  SingleSelect,
  TextInputWithDropdown,
  TimeAMPM,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { getBillingOptions } from "./RecurringSection";
import { ApplicationStatus, ItemBillingEnds } from "../enums";
import { DescriptionCell, InvoicePopupWrapper } from "../styles";
import RenderContactInfo from "./RenderContactInfo";
import { AccountSettingsStore } from "../store";
import MerchantAccountsDropDown from "./MerchantAccountsDropDown";
import PaymentMethodDropdown from "./PaymentMethodDropdown";
import RenderPaymentMethod from "./RenderPaymentMethod";
import { NMIPaymentMethodFormHandle } from "./NMIPaymentMethodForm";

type SingleValue =
  | string
  | boolean
  | Date
  | ContactReferenceModel
  | MerchantAccount
  | PaymentMethodModel
  | number
  | PurchasePaymentIntentModel;
type ArrayValue =
  | MerchantAccount[]
  | ContactReferenceModel[]
  | PaymentMethodModel[];
type FieldValue = SingleValue | ArrayValue;

type IProps = {
  onClose: (refresh: boolean) => void;
  subscriptionId?: string;
  subscriptionDetails: SubscriptionDetailModel;
};

const ResumeSubscriptionPopup: React.FC<IProps> = ({
  onClose,
  subscriptionDetails,
  subscriptionId,
}) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [resumeData, setResumeData] = useState<ResumeSubscriptionModel>(
    ResumeSubscriptionModel.deserialize(subscriptionDetails)
  );
  const [showLoader, setShowLoader] = React.useState(true);
  const formRef = useRef<NMIPaymentMethodFormHandle>(null);

  const [shouldSubmit, setShouldSubmit] = useState(false);
  useEffect(() => {
    if (shouldSubmit) {
      onClickSave(true);
      setShouldSubmit(false); // reset
    }
  }, [shouldSubmit]);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setShowLoader(true);
      await Promise.all([fetchMerchantAccounts(), fetchPaymentMethods()]);
    } catch (error) {
      console.error("Error loading subscription data:", error);
      ToastService.showError("Error loading data");
    } finally {
      setShowLoader(false);
    }
  };

  const fetchMerchantAccounts = async () => {
    const response = await MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      ""
    ).toPromise();

    if (response?.result) {
      const activeMerchantAccounts = (
        response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
        []
      ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

      const defaultMerchantAccount = MerchantAccount.getDefaultMerchantAccount(
        activeMerchantAccounts
      );

      setResumeData(
        (prevData) =>
          new ResumeSubscriptionModel({
            ...prevData,
            selectedMerchantAccount:
              prevData.selectedMerchantAccount.name === "" &&
              defaultMerchantAccount
                ? defaultMerchantAccount
                : prevData.selectedMerchantAccount,
            merchantAccounts: activeMerchantAccounts,
          })
      );
    }
  };

  const fetchPaymentMethods = async () => {
    const response = await PaymentMethodService.getPaymentMethods().toPromise();

    if (response?.result) {
      const paymentMethods = response.result.map((paymentMethod) =>
        PaymentMethodModel.deserializeAPIModel(paymentMethod)
      );

      const selectedPaymentMethod =
        PaymentMethodModel.getDefaultSelecedPaymentMethod(
          paymentMethods,
          subscriptionDetails.contact?.id || "",
          subscriptionDetails.selectedMerchantAccount?.merchantAccountId || ""
        );

      setResumeData(
        (prevData) =>
          new ResumeSubscriptionModel({
            ...prevData,
            paymentMethods: paymentMethods,
            selectedPaymentMethod:
              prevData.selectedPaymentMethod || selectedPaymentMethod,
          })
      );
    }
  };

  const row = (
    label: string,
    rightSide: React.ReactNode,
    spacing: number = 20,
    className?: string
  ) => {
    return (
      <FlexContainer spacing={30} className={className}>
        <CustomLabel>{label}</CustomLabel>
        <div>
          <FlexContainer alignItems="center" spacing={spacing}>
            {rightSide}
          </FlexContainer>
        </div>
      </FlexContainer>
    );
  };

  const singleSelect = (
    field: keyof ResumeSubscriptionModel,
    selectedKey: string,
    className: string,
    options: IOption[],
    error: boolean,
    errorMessage: string
  ): React.ReactNode => {
    return (
      <SingleSelect
        selectedKey={selectedKey}
        hasError={error}
        searchable={false}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options || []]}
        errorMessage={errorMessage}
        onChange={(option: any) => handleInputChange(field, option.value)}
        className={`${className} dpdn-no-separator`}
      />
    );
  };

  const textInput = (
    field: keyof ResumeSubscriptionModel,
    type: string,
    value: string | Date,
    className: string,
    error: boolean,
    placeholder: string,
    minDate?: Date
  ) => {
    return (
      <Input
        type={type as "text" | "textarea" | "number" | "date"}
        value={value}
        error={error}
        errorMessage={""}
        className={`styled-input merchant-inputs ${className}`}
        onChange={(e, isBlur) =>
          handleInputChange(field, e.target.value, isBlur)
        }
        placeholder={placeholder}
        onDateChange={(date) => handleInputChange(field, new Date(date))}
        minDate={minDate}
      />
    );
  };

  const handleInputChange = (
    field: keyof ResumeSubscriptionModel,
    value: FieldValue,
    isBlur?: boolean
  ) => {
    setResumeData((prevData) => {
      return new ResumeSubscriptionModel({ ...prevData, [field]: value });
    });
  };

  const billingEndsContent = () => {
    const billingEndsSelect = singleSelect(
      "billingEnds",
      resumeData.billingEnds,
      "number-selector subs-billing-end-dpdn",
      getBillingOptions("billingEnds"),
      resumeData.hasError("billingEnds"),
      ""
    );

    if (resumeData.billingEnds === ItemBillingEnds.After) {
      return (
        <FlexContainer fxLayout="column">
          <FlexContainer spacing={15} fxLayout="row" className="price-field">
            {billingEndsSelect}
            {textInput(
              "billingEndIntervalCount",
              "number",
              resumeData.billingEndIntervalCount.toString(),
              "number-selector subs-billing-count",
              resumeData.hasError("billingEndIntervalCount"),
              ""
            )}
            {singleSelect(
              "billingEndsAfterInterval",
              resumeData.billingEndsAfterInterval,
              "number-selector subs-billing-dpdn",
              getBillingOptions(
                "billingEndsAfterInterval",
                resumeData.billingEndIntervalCount > 1
              ),
              resumeData.hasError("billingEndsAfterInterval"),
              ""
            )}
          </FlexContainer>
        </FlexContainer>
      );
    }

    return <FlexContainer fxLayout="column">{billingEndsSelect}</FlexContainer>;
  };

  function onNewPaymentMethod(isNewPaymentMethod: boolean, token: string) {
    if (
      isNewPaymentMethod === resumeData.isNewPaymentMethod &&
      token === resumeData.newPaymentGatewayMethodId
    )
      return;
    setResumeData(
      (prevData) =>
        new ResumeSubscriptionModel({
          ...prevData,
          isNewPaymentMethod: isNewPaymentMethod,
          newPaymentGatewayMethodId: token,
        })
    );

    if (
      token &&
      isNewPaymentMethod &&
      resumeData.selectedMerchantAccount?.isNMIMerchantAccount()
    ) {
      setShouldSubmit(true);
    }
  }

  function updateTime(
    amPm: keyof typeof TimeAMPM,
    time: string,
    isBlur: boolean
  ) {
    if (time) {
      time = time.replace(/[^0-9:]/g, ""); // Remove non-digits and colons
      if (isBlur) {
        const match = time.match(/(\d{1,2}):(\d{1,2})/);
        if (match) {
          let [_, hours, minutes] = match;
          // Ensure hours and minutes are zero-padded
          hours = hours.padStart(2, "0");
          minutes = minutes.padStart(2, "0");

          // Return the formatted time string
          time = `${hours}:${minutes}`;
        }
      }
    }
    setResumeData(
      (prevData) =>
        new ResumeSubscriptionModel({
          ...prevData,
          resumeDateTime: time,
          resumeDateMeredium: amPm,
        })
    );
  }

  const renderSubscriptionContent = () => {
    return (
      <InvoicePopupWrapper className="subscription-popup resume-subscription">
        {resumeData.id && subscriptionDetails.contact?.id && (
          <FlexContainer spacing={30}>
            <CustomLabel lineHeight="22px !important">Customer</CustomLabel>
            <div>
              <RenderContactInfo contact={subscriptionDetails.contact} />
            </div>
          </FlexContainer>
        )}
        {row(
          "Description",
          <FlexContainer>
            <CustomLabel className="lh-40">
              {subscriptionDetails.description}
            </CustomLabel>
          </FlexContainer>
        )}
        {row(
          "Subscription No.",
          <FlexContainer>
            <CustomLabel className="lh-40">
              {subscriptionDetails.subscriptionNumber}
            </CustomLabel>
          </FlexContainer>
        )}
        {row(
          "Last Paid",
          !subscriptionDetails.lastPayment ? (
            <CustomLabel className="lh-40">-</CustomLabel>
          ) : (
            <>
              <FlexContainer spacing={20} alignItems="center">
                <CustomLabel className="lh-40">
                  $
                  {AccountSettingsStore.formatAmount(
                    subscriptionDetails.lastPayment.amount,
                    true
                  )}{" "}
                  on{" "}
                  {UtilityMethods.formatDateInString(
                    subscriptionDetails.lastPayment.createdDate
                  )}
                </CustomLabel>
                <RenderPaymentMethod
                  paymentMethod={subscriptionDetails.lastPayment.paymentMethod}
                />
              </FlexContainer>
            </>
          )
        )}
        {row(
          "Subscription Paused",
          <FlexContainer>
            <CustomLabel className="lh-40">
              {!resumeData.pausedDate
                ? "-"
                : UtilityMethods.formatDateInString(resumeData.pausedDate)}
            </CustomLabel>
          </FlexContainer>,
          20,
          ""
        )}
        {row(
          "Recurring Price",
          <FlexContainer>
            <CustomLabel className="lh-40">
              {subscriptionDetails.recurringPrice}
            </CustomLabel>
          </FlexContainer>,
          20
        )}
        {subscriptionDetails.pastDueAmount > 0 &&
          row(
            "Past Due",
            <FlexContainer fxLayout="column" padding="8px 0px 0px">
              <div>
                <FlexContainer spacing={40} alignItems="center">
                  <div className="red-text">
                    {"$" +
                      AccountSettingsStore.formatAmount(
                        subscriptionDetails.pastDueAmount
                      )}
                    <span className="font-regular"> USD</span>
                  </div>
                  <Input
                    type="checkbox"
                    value={resumeData.isCollectPastDue}
                    onChange={(e) =>
                      handleInputChange("isCollectPastDue", e.target.checked)
                    }
                    label={"Collect Past Due"}
                    className={"collect-past-due"}
                  />
                </FlexContainer>
              </div>
              {resumeData.isCollectPastDue && (
                <PriceDescriptionBlock>
                  <div>
                    <FlexContainer className="price-flex">
                      <CustomLabel color="#000">
                        $
                        {AccountSettingsStore.formatAmount(
                          parseFloat(subscriptionDetails.recurringPrice) +
                            subscriptionDetails.pastDueAmount || 0,
                          true
                        )}
                      </CustomLabel>
                      <div className="width-inherit">
                        <FlexContainer className="price-sub-flex">
                          <CustomLabel>Due</CustomLabel>
                          <CustomLabel fontWeight={"400"}>
                            {resumeData.resumeDate &&
                              UtilityMethods.formatDateInString(
                                resumeData.resumeDate
                              )}
                          </CustomLabel>
                        </FlexContainer>
                      </div>
                    </FlexContainer>
                  </div>
                  <div>
                    <FlexContainer className="price-flex">
                      <CustomLabel color="#000">
                        $
                        {AccountSettingsStore.formatAmount(
                          parseFloat(subscriptionDetails.recurringPrice) || 0,
                          true
                        )}
                      </CustomLabel>
                      <div className="width-inherit">
                        <FlexContainer fxLayout="column" spacing={5}>
                          <div className="width-inherit">
                            <FlexContainer className="price-sub-flex">
                              <CustomLabel>Starts</CustomLabel>
                              <CustomLabel fontWeight={"400"}>
                                {resumeData.resumeDate &&
                                  UtilityMethods.formatDateInString(
                                    calculateRenewalDate(
                                      resumeData.resumeDate,
                                      resumeData.billingInterval,
                                      resumeData.billingIntervalCount
                                    )
                                  )}
                              </CustomLabel>
                            </FlexContainer>
                          </div>
                          <div className="width-inherit">
                            <FlexContainer className="price-sub-flex">
                              <CustomLabel>Recurs</CustomLabel>
                              <CustomLabel fontWeight={"400"}>
                                Every {resumeData.billingIntervalCount}{" "}
                                {resumeData.billingInterval}
                              </CustomLabel>
                            </FlexContainer>
                          </div>
                          <div className="width-inherit">
                            <FlexContainer className="price-sub-flex">
                              <CustomLabel>Ends</CustomLabel>
                              <CustomLabel fontWeight={"400"}>
                                {resumeData.billingEnds ===
                                ItemBillingEnds.After
                                  ? `After ${resumeData.billingEndIntervalCount.toString()} ${resumeData.billingEndsAfterInterval}${resumeData.billingEndIntervalCount > 1 ? "s" : ""}`
                                  : "On Cancellation"}
                              </CustomLabel>
                            </FlexContainer>
                          </div>
                        </FlexContainer>
                      </div>
                    </FlexContainer>
                  </div>
                </PriceDescriptionBlock>
              )}
            </FlexContainer>,
            20,
            "invoice-payment-due"
          )}
        {!resumeData.selectedMerchantAccount &&
          resumeData.merchantAccounts.length > 1 &&
          row(
            "Merchant Account",
            <MerchantAccountsDropDown
              selectedMerchantAccount={resumeData.selectedMerchantAccount}
              merchantAccounts={resumeData.merchantAccounts}
              showError={resumeData.hasError("selectedMerchantAccount")}
              onChange={(option) =>
                handleInputChange("selectedMerchantAccount", option)
              }
              className="subscription-merchant-account"
            />
          )}
        {row(
          "Payment Method",
          <PaymentMethodDropdown
            ref={formRef}
            merchantAccount={resumeData.selectedMerchantAccount}
            selectedPaymentMethod={resumeData.selectedPaymentMethod}
            contactId={
              subscriptionDetails.contact?.id ||
              subscriptionDetails.contact?.mindmeContactId ||
              ""
            }
            paymentMethods={resumeData.paymentMethods.filter(
              (x) => x.contactId === subscriptionDetails.contact?.id
            )}
            onChange={(option) =>
              handleInputChange("selectedPaymentMethod", option)
            }
            hasError={resumeData.hasError("selectedPaymentMethod")}
            className="invoice-payment-method"
            contact={subscriptionDetails.contact || null}
            hasErrorInNewPaymentMethod={resumeData.hasError(
              "newPaymentGatewayMethodId"
            )}
            onNewPaymentMethod={(
              isNewPaymentMethod: boolean,
              token: string
            ) => {
              onNewPaymentMethod(isNewPaymentMethod, token);
            }}
            isLodingInProcess={showLoader}
            headerClass="subscription-payment-method"
            updateNewMethodAsPrimary={(isNewPaymentMethodPrimary) =>
              handleInputChange(
                "isNewPaymentMethodPrimary",
                isNewPaymentMethodPrimary
              )
            }
            isNewPaymentMethodPrimary={resumeData.isNewPaymentMethodPrimary}
          />
        )}
        {row(
          "Subscription Resumes",
          <div>
            <FlexContainer fxLayout="column" spacing={5}>
              <div>
                <FlexContainer alignItems="center" spacing={15}>
                  {textInput(
                    "resumeDate",
                    "date",
                    UtilityMethods.toLocalISOString(resumeData.resumeDate),
                    "invoice-payment-datepicker",
                    resumeData.hasError("resumeDate"),
                    "",
                    new Date()
                  )}
                  <TextInputWithDropdown
                    textInputValue={resumeData.resumeDateTime}
                    dropdownValue={resumeData.resumeDateMeredium}
                    onTextChange={(value, isBlur) =>
                      updateTime(resumeData.resumeDateMeredium, value, isBlur)
                    }
                    onDropdownChange={(option) =>
                      updateTime(option.value, resumeData.resumeDateTime, false)
                    }
                    className="invoice-timezone subscription-timezone"
                    dropdownOptions={[
                      Object.keys(TimeAMPM).map((key) => ({
                        value: key,
                        label: TimeAMPM[key as keyof typeof TimeAMPM],
                      })),
                    ]}
                    error={resumeData.hasError("resumeDateTime")}
                    placeholderForTextInput="hh:mm"
                    isTime={true}
                  />
                  <FlexContainer alignItems="center">
                    <CustomLabel>
                      {AccountSettingsStore.generalSettings.timeZone
                        .abbreviation || ""}
                    </CustomLabel>
                  </FlexContainer>
                </FlexContainer>
              </div>
            </FlexContainer>
          </div>,
          20,
          "invoice-payment-due"
        )}
        {row(
          "Bill Every",
          <DescriptionCell>
            <FlexContainer spacing={15} fxLayout="row" className="price-field">
              {textInput(
                "billingIntervalCount",
                "number",
                resumeData.billingIntervalCount === 0
                  ? ""
                  : resumeData.billingIntervalCount.toString(),
                "number-selector bill-every-input",
                resumeData.hasError("billingIntervalCount"),
                ""
              )}
              {singleSelect(
                "billingInterval",
                resumeData.billingInterval,
                "number-selector bill-every-dpdn",
                getBillingOptions(
                  "billingInterval",
                  resumeData.billingIntervalCount > 1
                ),
                resumeData.hasError("billingInterval"),
                ""
              )}
            </FlexContainer>
          </DescriptionCell>
        )}
        {row(
          "Billing Ends",
          <DescriptionCell>
            <FlexContainer>{billingEndsContent()}</FlexContainer>
          </DescriptionCell>
        )}
      </InvoicePopupWrapper>
    );
  };

  const onClickSave = (hasValidNMIPaymentMethod = false) => {
    if (isAPIRequestPending) return;

    //ONLY DO IT FOR NMI AS FOR NMI WE NEED TO WAIT FOR PAYMENT TOKEN AFTER validateCardDetails
    if (
      resumeData.isNewPaymentMethod &&
      !hasValidNMIPaymentMethod &&
      resumeData.selectedMerchantAccount?.isNMIMerchantAccount()
    ) {
      formRef.current?.validateCardDetails();
      return;
    }
    onSubmit();
  };

  function onSubmit() {
    setAPIRequestPending(true);
    const isValidated = resumeData.validate(false);
    if (isValidated) {
      const apiModelData = resumeData.createResumeAPIModel();
      SubscriptionService.resumeSubscription(apiModelData).subscribe({
        next: (response) => {
          // result is an object, so `if (response.result)` was always true (F07).
          if (response.result?.success) {
            onClose(true);
            ToastService.showSuccess("Subscription updated successfully");
          } else {
            ToastService.showError("Error in updating subscription");
          }
          setAPIRequestPending(false);
        },
        error: () => {
          setAPIRequestPending(false);
          ToastService.showError("Error in updating subscription");
        },
      });
    } else {
      setAPIRequestPending(false);
      setResumeData(new ResumeSubscriptionModel(resumeData));
    }
  }

  return (
    <>
      <ModalComponent
        title={`Resume Subscription`}
        titleFontSize="22px"
        bodyPadding="40px 50px"
        showFooter={true}
        minHeight="440px"
        wrapperWidth="900px"
        bodyBackgroundColor="#fefefe"
        footerBackground="#F4F7FB"
        footerHeight="auto"
        onClose={() => onClose(false)}
        footerContent={
          <FlexContainer
            alignItems="center"
            justifyContent="center"
            spacing={15}
          >
            <SapphireBlueButton
              minWidth="120px"
              height="30px"
              onClick={() => onClose(false)}
            >
              Close
            </SapphireBlueButton>
            <SapphireBlueButton
              primary
              minWidth="120px"
              height="30px"
              onClick={() => onClickSave()}
              disabled={isAPIRequestPending}
            >
              {"Update"}
            </SapphireBlueButton>
          </FlexContainer>
        }
      >
        {showLoader
          ? new Loader(true).loaderComponent
          : renderSubscriptionContent()}
      </ModalComponent>
    </>
  );
};

export default observer(ResumeSubscriptionPopup);
